import {
  Box,
  Stack,
  FormHelperText,
  FormHelperTextProps,
  FormLabelProps,
  FilledInputProps,
  OutlinedInputProps,
  TextFieldProps as MuiProps,
  useFormControl,
  Fade,
} from "@mui/material";
import { Theme } from "@mui/material/styles";
import { useUpdateEffect } from "@react-hookz/web";
import { ChangeEvent, FocusEvent, ReactElement, ReactNode, Ref, useState } from "react";
import { FormField } from "../FormField/FormField";
import { TextInput, TextInputProps } from "../TextInput/TextInput";

/** @deprecated - Use {@link TextInput} and {@link FormControl} instead */
export type TextFieldProps = Omit<
  MuiProps,
  | "name"
  | "label"
  | "helperText"
  | "variant"
  | "error"
  | "select"
  | "InputLabelProps"
  | "FormHelperTextProps"
  | "slotProps"
> & {
  name: string;
  label?: string;
  helperText?: ReactNode;
  cornerHelperText?: ReactNode;
  leftCornerElement?: ReactNode;
  topHelperText?: ReactNode;
  showCount?: boolean;
  maxLength?: number;
  tooltip?: ReactNode;
  readOnly?: boolean;
  error?: boolean | string;
  variant?: "outlined" | "filled";
  controlRef?: Ref<unknown>;
  InputLabelProps?: FormLabelProps;
  FormHelperTextProps?: FormHelperTextProps;
  slotProps?: {
    input?: Partial<FilledInputProps> | Partial<OutlinedInputProps>;
    htmlInput?: React.InputHTMLAttributes<HTMLInputElement>;
    inputLabel?: FormLabelProps;
    formHelperText?: FormHelperTextProps;
  };
};

type CountProps = { charCount: number; maxLength?: number };

const Count = ({ charCount, maxLength }: CountProps): ReactElement => {
  const { focused } = useFormControl() || {};

  return (
    // We need to Fade the Count out to avoid issues with content instantly moving and causing clicks to miss a component.
    <Fade in={focused} unmountOnExit>
      <FormHelperText sx={{ color: (theme: Theme) => theme.vars.palette.primary.light }}>
        {[charCount, maxLength].filter((x) => x != null).join(" / ")}
      </FormHelperText>
    </Fade>
  );
};

/** @deprecated - Use {@link TextInput} with {@link FormControl} instead */
export const TextField = ({
  id: inId,
  autoComplete = "off",
  size,
  variant = "outlined",
  autoFocus,
  showCount,
  maxLength,
  defaultValue,
  fullWidth,
  multiline,
  rows,
  minRows,
  maxRows,
  type,
  value,
  name,
  label,
  error,
  helperText,
  cornerHelperText,
  topHelperText,
  leftCornerElement,
  tooltip,
  inputRef,
  controlRef,
  placeholder,
  readOnly,
  required,
  disabled,
  onBlur,
  onChange,
  onFocus,
  inputProps,
  InputProps,
  InputLabelProps,
  FormHelperTextProps,
  slotProps,
}: TextFieldProps): ReactElement => {
  const [charCount, setCharCount] = useState(() => {
    if (typeof value === "string") return value.length;
    if (typeof defaultValue === "string") return defaultValue.length;
    return 0;
  });

  const id = inId || name;
  const helperTextId = helperText && id ? `${id}-helper-text` : undefined;
  const slotInputProps = slotProps?.input;
  const slotHtmlInputProps = slotProps?.htmlInput;
  const slotInputLabelProps = slotProps?.inputLabel;
  const slotFormHelperTextProps = slotProps?.formHelperText;
  const labelSx = [
    InputLabelProps?.sx,
    slotInputLabelProps?.sx,
    {
      color: "text.primary",
      "&.Mui-focused:not(.Mui-error)": {
        color: "text.primary",
      },
    },
  ].filter(Boolean) as FormLabelProps["sx"];

  useUpdateEffect(() => {
    setCharCount(typeof value === "string" ? value.length : 0);
  }, [value]);

  const handleChange = (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    onChange?.(event);
    setCharCount(event.target.value.length);
  };

  const handleBlur = (event: FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    onBlur?.(event);
    setCharCount(event.target.value.length);
  };

  return (
    <FormField
      name={name}
      id={id}
      label={label}
      cornerHelperText={cornerHelperText}
      topHelperText={topHelperText}
      tooltip={tooltip}
      required={required}
      disabled={disabled}
      error={error}
      fullWidth={fullWidth}
      controlRef={controlRef}
      FormControlComponent="div"
      FormHelperTextProps={{
        id: helperTextId,
        ...FormHelperTextProps,
        ...slotFormHelperTextProps,
      }}
      FormLabelProps={{
        ...InputLabelProps,
        ...slotInputLabelProps,
        sx: labelSx,
      }}
    >
      <TextInput
        size={size}
        variant={variant}
        aria-describedby={helperTextId}
        autoComplete={autoComplete}
        // eslint-disable-next-line jsx-a11y/no-autofocus -- allow consumers to opt in so focus transfers to first field in dialogs or inline editors
        autoFocus={autoFocus}
        defaultValue={defaultValue}
        fullWidth={fullWidth}
        multiline={multiline}
        inputProps={{
          maxLength,
          "data-testid": `${id}-input`,
          ...inputProps,
          ...slotHtmlInputProps,
        }}
        name={name}
        rows={rows}
        maxRows={maxRows}
        minRows={minRows}
        type={type}
        value={value}
        id={id}
        inputRef={inputRef}
        readOnly={readOnly}
        onBlur={!readOnly ? handleBlur : undefined}
        onChange={!readOnly ? handleChange : undefined}
        onFocus={!readOnly ? onFocus : undefined}
        placeholder={placeholder}
        {...InputProps}
        {...slotInputProps}
        sx={
          {
            ...InputProps?.sx,
            ...slotInputProps?.sx,
          } as TextInputProps["sx"]
        }
      />
      <Stack direction="row" justifyContent="space-between" gap={1} mt={leftCornerElement ? 2 : 0}>
        {leftCornerElement}
        {helperText ? (
          <FormHelperText
            component="div"
            id={helperTextId}
            {...FormHelperTextProps}
            error={false}
            sx={{ flex: 1 }}
          >
            {helperText}
          </FormHelperText>
        ) : null}
        {showCount && !readOnly ? (
          <Box ml="auto" data-testid={`${name}-count`}>
            <Count charCount={charCount} maxLength={maxLength} />
          </Box>
        ) : null}
      </Stack>
    </FormField>
  );
};
